import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Box, Typography } from '@mui/material';
import ErrorHandlingLayout from '@sharedCR/common/ErrorHandlingLayout';
import FormGenerator from '@sharedCR/common/formGenerator';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useForm from 'hooks/useForm';
import useLoanId from 'hooks/useLoanId';
import useRemoteFieldValueSync from 'hooks/useRemoteFieldValueSync';
import useTruncatedGeneral from 'hooks/useTruncatedGeneral';
import { getConfigInfo } from 'utils/loanManagement';
import { logger } from 'utils/logger';
import { checkIsPendingChangeHasDifference } from 'utils/pendingChanges';

import { getInvestorsAction, getServicingCompaniesAction } from 'store/accounting/actions';
import {
  selectInvestorsOptions,
  selectServicingCompaniesOptions,
} from 'store/accounting/selectors';
import { showMessageAction } from 'store/global/actions';
import { editPostClosingAction } from 'store/loanApplications/actions';
import {
  selectEntityChangedPropsById,
  selectIsPostClosingLoaded,
  selectIsUnsecuredFromPropertyAndLoanSummary,
  selectPostClosing,
} from 'store/loanApplications/selectors';

import { usePostClosingSelectOptions } from '../../hooks/usePostClosingSelectOptions';
import { getTrackingInfo } from '../../trackingNumber.utils';
import { DEFAULT_SECTION, SECTION_HREF_MAP, SECTION_NAME_MAP } from '../EditDrawer.constants';
import { EditDrawerTestIds } from '../EditDrawer.testIds';
import {
  buildPostClosingApplicationData,
  buildPostClosingRequestBody,
  getVisibleSectionHrefs,
  resolvePostClosingSectionConfig,
} from '../EditDrawer.utils';

import { formWrapperStyles, sectionTitleStyles, wrapperStyles } from './Content.styles';
import { usePostClosingFormSeed } from './hooks/usePostClosingFormSeed';
import TrackingAdornment from './TrackingAdornment';

const Content = ({
  setIsFormChanged,
  isSaving,
  setIsLoading,
  onClose,
  setOnSave,
  additionalProps: { onCountPendingChanges, setPendingChanges, isPostClosingLoadFailed, onRetry },
}) => {
  const dispatch = useDispatch();
  const { subtab: section } = useParams();
  const loanId = useLoanId();

  const postClosing = useSelector(selectPostClosing);
  const isPostClosingLoaded = useSelector(selectIsPostClosingLoaded);
  const investorsOptions = useSelector(selectInvestorsOptions);
  const servicingCompaniesOptions = useSelector(selectServicingCompaniesOptions);
  const pendingChangesData = useSelector(selectEntityChangedPropsById(loanId));
  const isUnsecured = useSelector(selectIsUnsecuredFromPropertyAndLoanSummary);
  const postClosingSelectOptions = usePostClosingSelectOptions();

  const currentSection = section || DEFAULT_SECTION;
  const sectionTitle = SECTION_NAME_MAP[currentSection] || 'Edit Post Closing';
  const isValidSection = getVisibleSectionHrefs(isUnsecured).includes(currentSection);
  const isFormReady = isValidSection && isPostClosingLoaded;

  const [onMouseOver, onResetTooltipState, TruncatedProvider] = useTruncatedGeneral();

  const config = useMemo(() => {
    if (!isValidSection) {
      return { groups: [] };
    }

    return resolvePostClosingSectionConfig({
      sectionKey: currentSection,
      investorsOptions,
      servicingCompaniesOptions,
      isUnsecured,
      postClosingSelectOptions,
    });
  }, [
    currentSection,
    investorsOptions,
    servicingCompaniesOptions,
    isUnsecured,
    postClosingSelectOptions,
    isValidSection,
  ]);

  const applicationData = useMemo(
    () => buildPostClosingApplicationData(postClosing),
    [postClosing],
  );

  const { initialFormValues, fieldsConfig, observableFieldsNames, schema } = useMemo(
    () => getConfigInfo({ applicationData, config }),
    [applicationData, config],
  );

  const {
    form,
    handleChange,
    validationErrors,
    setInForm,
    isFormChanged,
    validateForm,
    validateField,
    setInitialFormValues,
    setForm,
    setSchema,
    setExcludedValidationPaths,
    formPendingChanges,
  } = useForm(initialFormValues, fieldsConfig, observableFieldsNames);

  const { remotePendingChanges, getCleanFormForSave, remoteInjectedFormKeys } =
    useRemoteFieldValueSync({
      config,
      setForm,
      setInitialFormValues,
    });

  useEffect(() => {
    setExcludedValidationPaths(remoteInjectedFormKeys);
  }, [remoteInjectedFormKeys, setExcludedValidationPaths]);

  const renderConfig = useMemo(() => {
    if (currentSection !== SECTION_HREF_MAP.SHIPPING) {
      return config;
    }

    return {
      ...config,
      groups: config.groups.map(group => ({
        ...group,
        fields: group.fields.map(field => {
          const value = form[field.nameInModel];

          if (_.isNil(value) || !value || value === '-') {
            return field;
          }

          const trackingInfo = getTrackingInfo(value);

          if (!trackingInfo) {
            return field;
          }

          return {
            ...field,
            inputProps: {
              ...field.inputProps,
              InputProps: {
                endAdornment: <TrackingAdornment trackingInfo={trackingInfo} />,
              },
            },
          };
        }),
      })),
    };
  }, [config, currentSection, form]);

  const formDataRef = usePostClosingFormSeed({
    form,
    initialFormValues,
    setInitialFormValues,
    setForm,
  });
  const visitedSectionsRef = useRef(new Set());

  useEffect(() => {
    if (!isFormReady) {
      return;
    }

    visitedSectionsRef.current.add(currentSection);
  }, [currentSection, isFormReady]);

  useEffectSafeStrictMode(() => {
    dispatch(getInvestorsAction());
    dispatch(getServicingCompaniesAction());
  }, [dispatch]);

  useEffect(() => {
    setSchema(schema);
  }, [setSchema, schema]);

  useEffect(() => {
    setIsFormChanged(isFormChanged);
  }, [isFormChanged, setIsFormChanged]);

  const filteredBackendPendingChanges = useMemo(() => {
    if (_.isNil(pendingChangesData)) {
      return {};
    }

    const filtered = {};
    const postClosingFieldNames = new Set(observableFieldsNames);

    Object.entries(pendingChangesData).forEach(([fieldName, pendingChange]) => {
      if (postClosingFieldNames.has(fieldName)) {
        filtered[fieldName] = pendingChange;
      }
    });

    return filtered;
  }, [pendingChangesData, observableFieldsNames]);

  const filteredFormPendingChanges = useMemo(() => {
    if (_.isNil(formPendingChanges)) {
      return {};
    }

    const filtered = {};

    Object.entries(formPendingChanges).forEach(([fieldName, pendingChange]) => {
      if (checkIsPendingChangeHasDifference(pendingChange)) {
        filtered[fieldName] = pendingChange;
      }
    });

    return filtered;
  }, [formPendingChanges]);

  const mergedPendingChanges = useMemo(() => {
    const merged = { ...filteredBackendPendingChanges };

    Object.entries(filteredFormPendingChanges).forEach(([fieldName, pendingChange]) => {
      merged[fieldName] = pendingChange;
    });

    Object.entries(remotePendingChanges).forEach(([fieldName, pendingChange]) => {
      merged[fieldName] = pendingChange;
    });

    return merged;
  }, [filteredBackendPendingChanges, filteredFormPendingChanges, remotePendingChanges]);

  useEffect(() => {
    if (onCountPendingChanges) {
      onCountPendingChanges(mergedPendingChanges, currentSection);
    }

    if (setPendingChanges) {
      setPendingChanges(mergedPendingChanges);
    }
  }, [mergedPendingChanges, currentSection, onCountPendingChanges, setPendingChanges]);

  const onSave = useCallback(async () => {
    if (!isPostClosingLoaded || !(await validateForm())) {
      return;
    }

    setIsLoading(true);

    const valuesToSend = buildPostClosingRequestBody({
      loanId,
      formData: getCleanFormForSave({ ...formDataRef.current, ...form }),
      postClosing,
      sectionKeys: visitedSectionsRef.current,
    });

    return dispatch(editPostClosingAction(valuesToSend))
      .then(() => {
        dispatch(
          showMessageAction({ message: 'Post Closing saved successfully', variant: 'success' }),
        );
        onClose();
      })
      .catch(error => {
        logger.error(error);
        dispatch(showMessageAction({ message: 'Failed to save Post Closing', variant: 'error' }));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    isPostClosingLoaded,
    validateForm,
    loanId,
    form,
    formDataRef,
    getCleanFormForSave,
    postClosing,
    dispatch,
    onClose,
    setIsLoading,
  ]);

  useEffect(() => {
    setOnSave(() => onSave);
  }, [setOnSave, onSave]);

  return (
    <Box sx={wrapperStyles}>
      <Typography sx={sectionTitleStyles} variant="bold20">
        {sectionTitle}
      </Typography>

      <ErrorHandlingLayout
        dataTestId={EditDrawerTestIds.LOAD_RETRY_BUTTON}
        isError={!!isPostClosingLoadFailed}
        onClick={onRetry}
      >
        {isFormReady && (
          <Box sx={formWrapperStyles} onMouseLeave={onResetTooltipState} onMouseOver={onMouseOver}>
            <TruncatedProvider />
            <FormGenerator
              config={renderConfig}
              form={form}
              formName={FormNames.POST_CLOSING}
              handleChange={handleChange}
              isLoading={isSaving}
              numberOfColumns={4}
              pendingChanges={mergedPendingChanges}
              setInForm={setInForm}
              validateField={validateField}
              validationErrors={validationErrors}
            />
          </Box>
        )}
      </ErrorHandlingLayout>
    </Box>
  );
};

Content.propTypes = {
  isSaving: PropTypes.bool.isRequired,
  setIsFormChanged: PropTypes.func.isRequired,
  setOnSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  additionalProps: PropTypes.shape({
    isPostClosingLoadFailed: PropTypes.bool,
    setPendingChanges: PropTypes.func,
    onCountPendingChanges: PropTypes.func,
    onRetry: PropTypes.func,
  }),
  ...WithLoadingPropTypes,
};

export default memo(withLoading(Content));
